import React, { useCallback, useEffect, useState } from 'react';
import { LayoutGrid, ShieldCheck, SquareStack, Users } from 'lucide-react';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import { SideNavMenu, SideNavItem } from 'back-office/shared/components/SideNavMenu';
import { atelierRolesAPI } from 'services/swagger-api';
import {
  GetReferentielBriquesResponse,
  GetProfilsResponse
} from 'api/atelier-roles';
import { RolesTab } from './RolesTab';
import { ProfilsTab } from './ProfilsTab';
import { PortailsTab } from './PortailsTab';

type SectionId = 'roles' | 'profils' | 'portails';

/**
 * L'Atelier des rôles (BO admin) : le référentiel des briques (standard
 * verrouillées / personnalisées administrables), les profils (figés, appliqués
 * à la création des acteurs) et les portails. Navigation par menu latéral,
 * comme la fiche plateforme. Les gardes (briques standard, garde de nature)
 * sont appliquées côté serveur — l'UI les reflète, jamais ne les remplace.
 */
export const AtelierRolesView: React.FC = () => {
  const [section, setSection] = useState<SectionId>('roles');
  const [referentiel, setReferentiel] = useState<GetReferentielBriquesResponse | null>(null);
  const [profils, setProfils] = useState<GetProfilsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const [briques, profilsData] = await Promise.all([
        atelierRolesAPI.getReferentiel(),
        atelierRolesAPI.getProfils()
      ]);
      setReferentiel(briques);
      setProfils(profilsData);
    } catch (err) {
      console.error("Erreur de chargement de l'atelier des rôles:", err);
      setError('Impossible de charger le référentiel — réessayez.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const menuItems: SideNavItem[] = [
    { id: 'roles', label: 'Rôles', icon: SquareStack, badge: referentiel?.briques.length },
    { id: 'profils', label: 'Profils', icon: Users, badge: profils?.profils.length },
    { id: 'portails', label: 'Portails', icon: LayoutGrid, badge: referentiel?.portails.length }
  ];

  return (
    <SimpleDashboardContent>
      <GenericViewHeader title="Gestion des rôles" icon={ShieldCheck} section="Paramètres" />

      <section className="w-full overflow-hidden border bg-white rounded-2xl">
        <div className="p-8 shadow-inner">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {isLoading || !referentiel || !profils ? (
            <div className="py-16 text-center text-sm text-gray-500">Chargement…</div>
          ) : (
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-56">
                <SideNavMenu
                  items={menuItems}
                  activeId={section}
                  onSelect={(id) => setSection(id as SectionId)}
                />
              </div>

              <div className="flex-1 min-w-0">
                {section === 'roles' && <RolesTab referentiel={referentiel} onChanged={reload} />}
                {section === 'profils' && <ProfilsTab profils={profils} />}
                {section === 'portails' && <PortailsTab referentiel={referentiel} onChanged={reload} />}
              </div>
            </div>
          )}
        </div>
      </section>
    </SimpleDashboardContent>
  );
};

export default AtelierRolesView;
